import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  type TallyFormField,
  type TallyFormSummary,
  type TallyMappingRule,
  useTallyConnection,
} from "@/hooks/useTallyConnection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Loader2,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const TARGETS = [
  ["ignore", "Do not absorb"],
  ["guardian.name", "Guardian / customer name"],
  ["guardian.email", "Guardian / customer email"],
  ["guardian.phone", "Guardian / customer phone"],
  ["guardian.relationship", "Guardian relationship"],
  ["student.full_name", "Participant 1: name"],
  ["student.level", "Participant 1: level"],
  ["student.date_of_birth", "Participant 1: date of birth"],
  ["student.notes", "Participant 1: notes"],
  ...Array.from({ length: 4 }, (_, offset) => {
    const index = offset + 1;
    const label = index + 1;
    return [
      [`student[${index}].full_name`, `Participant ${label}: name`],
      [`student[${index}].level`, `Participant ${label}: level`],
      [
        `student[${index}].date_of_birth`,
        `Participant ${label}: date of birth`,
      ],
      [`student[${index}].notes`, `Participant ${label}: notes`],
      [
        `student[${index}].profile.goal`,
        `Participant ${label}: learning goal`,
      ],
      [
        `student[${index}].profile.interests`,
        `Participant ${label}: interests`,
      ],
      [
        `student[${index}].profile.preferences`,
        `Participant ${label}: preferences`,
      ],
    ] as const;
  }).flat(),
  ["student[0].profile.goal", "Participant 1: learning goal"],
  ["student[0].profile.interests", "Participant 1: interests"],
  ["student[0].profile.preferences", "Participant 1: preferences"],
  ["profile.goal", "All participants: learning goal"],
  ["profile.interests", "All participants: interests"],
  ["profile.preferences", "All participants: preferences"],
  ["analytics.program", "Analytics: program"],
  ["analytics.source", "Analytics: acquisition source"],
  ["consent.marketing", "Consent: marketing"],
] as const;

type Mapping = Record<string, TallyMappingRule>;

export default function TallyConnectionCard({ className }: { className?: string }) {
  const { businessId } = useAuth();
  const tally = useTallyConnection(businessId);
  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [remoteForms, setRemoteForms] = useState<TallyFormSummary[]>([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [fields, setFields] = useState<TallyFormField[]>([]);
  const [mapping, setMapping] = useState<Mapping>({});
  const [liveAutomation, setLiveAutomation] = useState(true);
  const [backfillAutomation, setBackfillAutomation] = useState(false);
  const [retentionDays, setRetentionDays] = useState(7);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [stagedConnectionId, setStagedConnectionId] = useState("");
  const hasStableIdentity = useMemo(
    () =>
      Object.values(mapping).some((rule) =>
        rule.target === "guardian.email" ||
        rule.target === "guardian.phone"
      ),
    [mapping],
  );

  const activeConnection = useMemo(
    () =>
      tally.state?.connections?.find((row) => row.status !== "disconnected"),
    [tally.state?.connections],
  );
  const connected = activeConnection?.status === "connected";
  const connectionId = activeConnection?.id ?? stagedConnectionId;

  const act = async (
    action: string,
    input?: Record<string, unknown>,
    success?: string,
  ) => {
    try {
      const data = await tally.mutateAsync({ action, input });
      if (success) toast.success(success);
      return data;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Tally request failed",
      );
      return undefined;
    }
  };

  const connect = async () => {
    if (!apiKey.trim()) return;
    const data = await act("connect", { api_key: apiKey.trim() });
    if (!data) return;
    setStagedConnectionId(String(data.connection_id ?? ""));
    setApiKey("");
    setRemoteForms((data?.forms ?? []) as TallyFormSummary[]);
    setConnectOpen(false);
    setConfigureOpen(true);
    toast.success(
      "Tally connected. Choose a form and map only the fields Dibs should absorb.",
    );
  };

  const listForms = async () => {
    if (!connectionId) return;
    const data = await act("list_forms", {
      connection_id: connectionId,
    });
    if (!data) return;
    setRemoteForms((data?.forms ?? []) as TallyFormSummary[]);
    setConfigureOpen(true);
  };

  const loadSchema = async (formId: string) => {
    setSelectedFormId(formId);
    if (!formId) return;
    if (!connectionId) {
      toast.error("The Tally connection is still loading. Please try again.");
      return;
    }
    setLoadingSchema(true);
    try {
      const data = await act("form_schema", {
        connection_id: connectionId,
        form_id: formId,
      });
      if (!data) return;
      const nextFields = (data?.fields ?? []) as TallyFormField[];
      setFields(nextFields);
      const configured = tally.state?.forms?.find(
        (form) => form.tally_form_id === formId,
      );
      setMapping(
        configured?.field_mapping ??
          guessMapping(nextFields),
      );
      if (configured) {
        setLiveAutomation(configured.live_automation_enabled !== false);
        setBackfillAutomation(configured.backfill_automation_enabled === true);
        setRetentionDays(Number(configured.raw_retention_days ?? 7));
      }
    } finally {
      setLoadingSchema(false);
    }
  };

  const configure = async () => {
    if (!selectedFormId || !connectionId) return;
    if (!hasStableIdentity) {
      toast.error(
        "Map at least one Tally email or phone field so repeat submissions resolve to one CRM profile.",
      );
      return;
    }
    const data = await act(
      "configure_form",
      {
        connection_id: connectionId,
        form_id: selectedFormId,
        field_mapping: mapping,
        live_automation_enabled: liveAutomation,
        backfill_automation_enabled: backfillAutomation,
        raw_retention_days: retentionDays,
      },
      "Tally form connected and signed webhook registered.",
    );
    if (!data) return;
    setConfigureOpen(false);
  };

  const patchRule = (key: string, patch: Partial<TallyMappingRule>) => {
    setMapping((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  };

  const disconnect = async () => {
    const result = await act(
      "disconnect",
      {
        connection_id: connectionId,
      },
      "Tally disconnected and credentials revoked.",
    );
    if (result) setDisconnectOpen(false);
  };

  if (tally.isLoading) {
    return (
      <div className={`flex items-center justify-center rounded-xl border bg-card p-6 ${className ?? ""}`}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tally.isError) {
    return (
      <div className={`flex items-center justify-between gap-4 rounded-xl border border-destructive/30 bg-card p-4 ${className ?? ""}`}>
        <div>
          <p className="text-sm font-medium">
            Tally connection status unavailable
          </p>
          <p className="text-xs text-muted-foreground">
            {tally.error instanceof Error
              ? tally.error.message
              : "Dibs could not load the connector state."}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void tally.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className={`space-y-4 rounded-xl border bg-card p-4 shadow-sm ${className ?? ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-foreground">
              <img
                src="/tally-icon.svg"
                alt=""
                aria-hidden="true"
                className="h-7 w-7"
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  Tally Forms
                </span>
                {connected && (
                  <Badge
                    variant="default"
                    className="border-transparent bg-green-600 text-white hover:bg-green-600"
                  >
                    Connected
                  </Badge>
                )}
                {connected && activeConnection.last_error && (
                  <Badge
                    variant="outline"
                    className="border-amber-300 text-amber-700"
                  >
                    MCP warning
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Turn Tally Form responses into structured Dibs CRM records. It does not modify Tally forms or create Dibs bookings.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Switch
              checked={connected}
              disabled={tally.busy}
              aria-label="Tally connection"
              onCheckedChange={(checked) => {
                if (checked) setConnectOpen(true);
                else setDisconnectOpen(true);
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-muted"
                  aria-label="Manage Tally connection"
                >
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {connected && (
                  <>
                    <DropdownMenuLabel className="font-normal text-muted-foreground">
                      {(tally.state?.forms?.length ?? 0) === 1
                        ? "1 connected form"
                        : `${tally.state?.forms?.length ?? 0} connected forms`}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                  </>
                )}
                {connected ? (
                  <>
                    <DropdownMenuItem
                      onClick={() => void listForms()}
                      disabled={tally.busy}
                    >
                      Manage forms
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDisconnectOpen(true)}
                      disabled={tally.busy}
                      className="text-destructive"
                    >
                      Disconnect
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem
                    onClick={() => setConnectOpen(true)}
                    disabled={tally.busy}
                  >
                    Connect Tally
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {(tally.state?.forms?.length ?? 0) > 0 && (
          <div className="space-y-2 border-t pt-3">
            {tally.state!.forms.map((form) => (
              <div
                key={form.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">
                      {form.form_name}
                    </p>
                    <Badge variant="outline">
                      {form.is_enabled ? "Live" : "Paused"}
                    </Badge>
                    {form.backfill_state === "running" ||
                    form.backfill_state === "queued" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {
                      Object.keys(form.field_mapping ?? {}).filter(
                        (key) => form.field_mapping[key]?.target !== "ignore",
                      ).length
                    }{" "}
                    mapped fields · raw payload retention{" "}
                    {form.raw_retention_days} days · backfill{" "}
                    {String(form.backfill_state).replace(/_/g, " ")}
                  </p>
                  {form.last_error && (
                    <p className="mt-1 text-xs text-destructive">
                      {form.last_error}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Switch
                    checked={form.is_enabled}
                    disabled={tally.busy}
                    aria-label={`${form.form_name} webhook`}
                    onCheckedChange={(enabled) =>
                      void act(
                        "toggle_form",
                        {
                          form_connection_id: form.id,
                          enabled,
                        },
                        enabled
                          ? "Tally webhook resumed."
                          : "Tally webhook paused.",
                      )
                    }
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 hover:bg-muted"
                        aria-label={`Manage ${form.form_name}`}
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          void act(
                            "backfill",
                            { form_connection_id: form.id },
                            "Historical import queued.",
                          )
                        }
                        disabled={
                          tally.busy ||
                          ["queued", "running"].includes(form.backfill_state)
                        }
                      >
                        <Database className="mr-2 h-4 w-4" />
                        Backfill historical responses
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          void act(
                            "reconcile",
                            { form_connection_id: form.id },
                            "Reconciliation queued.",
                          )
                        }
                        disabled={tally.busy}
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Reconcile submissions
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          void act(
                            "rotate_webhook_secret",
                            { form_connection_id: form.id },
                            "Tally webhook signing secret rotated.",
                          )
                        }
                        disabled={tally.busy}
                      >
                        Rotate webhook secret
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}

        {(tally.state?.failures?.length ?? 0) > 0 && (
          <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              Needs attention
            </div>
            {tally.state!.failures.slice(0, 5).map((failure) => (
              <div
                key={failure.id}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="text-amber-900">
                  {String(failure.status).replace(/_/g, " ")}:{" "}
                  {failure.last_error ??
                    failure.last_error_code ??
                    "Processing stopped"}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (
                      failure.status === "review_required" &&
                      !window.confirm(
                        "Retry this response only after the conflicting CRM identities have been corrected or merged. Continue?",
                      )
                    ) {
                      return;
                    }
                    void act(
                      "retry_submission",
                      { inbox_id: failure.id },
                      "Submission retry queued.",
                    );
                  }}
                >
                  {failure.status === "review_required"
                    ? "Retry after CRM fix"
                    : "Retry"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Tally?</AlertDialogTitle>
            <AlertDialogDescription>
              Dibs will revoke the stored credential and remove registered
              Tally webhooks. Existing normalized CRM records will remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={tally.busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void disconnect();
              }}
              disabled={tally.busy}
            >
              {tally.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect Tally</DialogTitle>
            <DialogDescription>
              Use a Tally API key for this pilot. Dibs validates it against
              Tally, stores it in Supabase Vault, and never returns it to the
              browser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="tally-api-key">Tally API key</Label>
            <Input
              id="tally-api-key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="Paste the Tally API key"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>
              Cancel
            </Button>
            <Button onClick={connect} disabled={!apiKey.trim() || tally.busy}>
              {tally.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Validate and connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={configureOpen} onOpenChange={setConfigureOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map a Tally form into Dibs</DialogTitle>
            <DialogDescription>
              Nothing is absorbed until you map it. Sensitive fields cannot be
              used for participant AI profiles, even if that switch is selected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Form</Label>
              <Select value={selectedFormId} onValueChange={loadSchema}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a Tally form" />
                </SelectTrigger>
                <SelectContent>
                  {remoteForms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name} ({form.numberOfSubmissions ?? 0} submissions)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loadingSchema && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}

            {!loadingSchema && fields.length > 0 && (
              <div className="space-y-2">
                {!hasStableIdentity && (
                  <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      Map at least one email or phone field. Dibs uses that
                      stable identifier to keep repeat submissions on one CRM
                      contact and participant history. Make this field required
                      in Tally so incomplete responses go to review instead of
                      creating a profile.
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-[minmax(150px,1fr)_minmax(180px,1fr)_auto] gap-3 px-2 text-xs font-medium text-muted-foreground">
                  <span>Tally field</span>
                  <span>Dibs destination</span>
                  <span>Uses</span>
                </div>
                {fields.map((field) => {
                  const rule = mapping[field.key] ?? { target: "ignore" };
                  return (
                    <div
                      key={field.key}
                      className="grid grid-cols-1 gap-3 rounded-lg border p-3 md:grid-cols-[minmax(150px,1fr)_minmax(180px,1fr)_auto]"
                    >
                      <div>
                        <p className="text-sm font-medium">{field.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {field.type}
                        </p>
                      </div>
                      <Select
                        value={rule.target}
                        onValueChange={(target) =>
                          patchRule(field.key, { target })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TARGETS.map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                        <Flag
                          label="CRM"
                          checked={rule.crm_use === true}
                          onChange={(crm_use) =>
                            patchRule(field.key, { crm_use })
                          }
                        />
                        <Flag
                          label="Analytics"
                          checked={rule.analytics_use === true}
                          onChange={(analytics_use) =>
                            patchRule(field.key, { analytics_use })
                          }
                        />
                        <Flag
                          label="AI profile"
                          checked={rule.ai_profile_use === true}
                          disabled={rule.sensitive === true}
                          onChange={(ai_profile_use) =>
                            patchRule(field.key, { ai_profile_use })
                          }
                        />
                        <Flag
                          label="Sensitive"
                          checked={rule.sensitive === true}
                          onChange={(sensitive) =>
                            patchRule(field.key, {
                              sensitive,
                              ...(sensitive ? { ai_profile_use: false } : {}),
                            })
                          }
                        />
                        <Input
                          className="col-span-2 h-7 text-xs"
                          value={rule.workflow_key ?? ""}
                          onChange={(event) =>
                            patchRule(field.key, {
                              workflow_key: event.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9_-]/g, "")
                                .slice(0, 100),
                            })
                          }
                          placeholder="Workflow key: program, level, goal"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 md:grid-cols-3">
              <Flag
                label="Run workflows for live responses"
                checked={liveAutomation}
                onChange={setLiveAutomation}
              />
              <Flag
                label="Run workflows during backfill"
                checked={backfillAutomation}
                onChange={setBackfillAutomation}
              />
              <div className="space-y-1">
                <Label className="text-xs">Raw payload retention</Label>
                <Select
                  value={String(retentionDays)}
                  onValueChange={(value) => setRetentionDays(Number(value))}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 3, 7, 14, 30].map((days) => (
                      <SelectItem key={days} value={String(days)}>
                        {days} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigureOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={configure}
              disabled={
                !selectedFormId ||
                !hasStableIdentity ||
                tally.busy ||
                loadingSchema
              }
            >
              {tally.busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Register signed webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Flag({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
      <span>{label}</span>
    </label>
  );
}

function guessRule(field: TallyFormField): TallyMappingRule {
  const haystack = `${field.label} ${field.key}`.toLowerCase();
  const base = { label: field.label, target: "ignore" };
  if (
    ["FILE_UPLOAD", "SIGNATURE", "PAYMENT"].includes(field.type.toUpperCase())
  ) {
    return { ...base, sensitive: true };
  }
  if (field.type.includes("EMAIL") || /\bemail\b/.test(haystack)) {
    return { ...base, target: "guardian.email", crm_use: true };
  }
  if (field.type.includes("PHONE") || /\bphone|mobile\b/.test(haystack)) {
    return { ...base, target: "guardian.phone", crm_use: true };
  }
  if (/parent|guardian/.test(haystack) && /\bname\b/.test(haystack)) {
    return { ...base, target: "guardian.name", crm_use: true };
  }
  if (
    /student|participant|child|learner/.test(haystack) &&
    /\bname\b/.test(haystack)
  ) {
    return { ...base, target: "student.full_name", crm_use: true };
  }
  if (/date of birth|dob|birthdate|birth year/.test(haystack)) {
    return {
      ...base,
      target: "student.date_of_birth",
      crm_use: true,
      sensitive: true,
    };
  }
  if (/level|grade|year/.test(haystack)) {
    return {
      ...base,
      target: "student.level",
      crm_use: true,
      analytics_use: true,
      ai_profile_use: true,
      workflow_key: "level",
    };
  }
  if (/goal|objective/.test(haystack)) {
    return {
      ...base,
      target: "profile.goal",
      crm_use: true,
      ai_profile_use: true,
      workflow_key: "goal",
    };
  }
  if (/program|course|class/.test(haystack)) {
    return {
      ...base,
      target: "analytics.program",
      crm_use: true,
      analytics_use: true,
      workflow_key: "program",
    };
  }
  return base;
}

function guessMapping(fields: TallyFormField[]): Mapping {
  const occurrences = new Map<string, number>();
  return Object.fromEntries(
    fields.map((field) => {
      const rule = guessRule(field);
      if (rule.target.startsWith("student.")) {
        const count = occurrences.get(rule.target) ?? 0;
        occurrences.set(rule.target, count + 1);
        if (count > 0) {
          const suffix = rule.target.slice("student.".length);
          rule.target = `student[${count}].${suffix}`;
          if (rule.workflow_key) {
            rule.workflow_key = `${rule.workflow_key}_${count + 1}`;
          }
        }
      }
      return [field.key, rule];
    }),
  );
}
